import {
  Component,
  OnInit,
  Input,
  ViewContainerRef,
  ViewChild,
  ComponentRef,
  Type,
  ComponentFactoryResolver,
  Output,
  EventEmitter,
  AfterViewInit
} from '@angular/core';
import { DataEntity } from '../../../../models/data-entities';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { DataEntityFactory, ValidationService } from '../../../../services';
import { Activity, ActivityModel } from '../../../../models/activities';
import { DataEntityViewComponent } from '../data-entity-view/data-entity-view.component';
import { distinctUntilChanged, Subscription } from 'rxjs';

@Component({
  selector: 'wm-data-entity-input',
  templateUrl: './data-entity-input.component.html',
  styleUrls: ['./data-entity-input.component.css']
})
export class DataEntityInputComponent implements OnInit, AfterViewInit {
  @Input() entity: DataEntity;
  @Input() form: UntypedFormGroup;
  @Input() applicationId: string;
  @Input() activity: Activity<ActivityModel>;
  @Input() isPreview: boolean;
  @Output() calculateActivity: EventEmitter<{
    templateCode: string;
    calculateFormula?: boolean;
    visReq?: boolean;
  }> = new EventEmitter();

  @ViewChild('entityContainer', { read: ViewContainerRef, static: true })
  entityContainer: ViewContainerRef;
  inputComponentRef: ComponentRef<DataEntityInputComponent>;
  viewComponentRef: ComponentRef<DataEntityViewComponent>;
  activities: string;
  // valueUpdatedSubscriber: Subscription;
  calculatingFormControlName = '';

  constructor(private componentFactoryResolver: ComponentFactoryResolver) {}
  async persistChildComponent?() {
    if (this.inputComponentRef) {
      return this.inputComponentRef.instance.persistChildComponent();
    } else {
      return new Promise(resolve => {
        resolve(null);
      });
    }
  }
  ngOnInit() {
    if (this.entity && this.entity.completedDependentActivtyInfo) {
      this.activities = this.entity.completedDependentActivtyInfo
        .map(m => m.activityLabel)
        .join(',');
    }
    this.loadEntity();

    this.calculatingFormControlName = this.entity.templateCode + '-calculating';

    if (this.form) {
      this.form.addControl(
        this.calculatingFormControlName,
        new UntypedFormControl('false', Validators.required)
      );
    }

    this.entity.isCalculating = false;
  }

  unlockEntity() {
    this.entity.isLocked = false;
    this.loadEntity();
  }

  ngAfterViewInit(): void {
    // watch for changes that are included in the formula for the data entity if there are some on the same activity
    if ((this.entity.sameActivityFormulaDataEntities || []).length > 0) {
      // check to see if there are changes ever second and calculate new value if there are changes
      this.watchForCalculation();
    }

    if(!this.entity.isVisible && this.form && this.form.controls[this.entity.templateCode]) {
      this.form.controls[this.entity.templateCode].disable();
    }
    else if(this.form && this.form.controls[this.entity.templateCode]) {
      this.form.controls[this.entity.templateCode].enable();
    }
  }

  fireCalculation() {
    let calcControl = this.form.controls[this.calculatingFormControlName];

    if (calcControl) {
      calcControl.setValue('');

      this.entity.isCalculating = true;

      this.calculateActivity.emit({
        templateCode: this.entity.templateCode,
        visReq: true
      });
    }
  }

  cancelCalculation() {
    if (this.form.controls[this.calculatingFormControlName]) {
      this.form.controls[this.calculatingFormControlName].setValue('false');
    }
    if (this.inputComponentRef) {
      this.inputComponentRef.instance.cancelCalculation();
    }
  }

  watchForCalculation() {
    const calcValue = changes => {
      this.fireCalculation();
    };

    setTimeout(() => {
      // watch for changes on the data entity that are used in the formula
      this.entity.sameActivityFormulaDataEntities.forEach((value, idx) => {
        if (this.form.controls[value]) {
          this.form.controls[value].valueChanges
            .pipe(distinctUntilChanged())
            .subscribe(calcValue);
        }
      });
    }, 100);
  }

  ngOnDestroy(): void {
    if (this.form) {
      this.form.removeControl(this.calculatingFormControlName);
    }
  }

  loadEntity() {
    const code = this.entity.dataEntityTypeCode;
    if (!(this.entity instanceof DataEntity)) {
      this.entity = DataEntityFactory.createDataEntity(code, this.entity);
    }
    if (this.entityContainer) {
      this.entityContainer.clear();

      if (this.entity.isLocked) {
        const viewCompType: Type<DataEntityViewComponent> = DataEntityFactory.createViewComponent(
          this.entity,
          this.entity
        );

        if (viewCompType && this.entityContainer) {
          this.viewComponentRef = this.entityContainer.createComponent(
            viewCompType
          );
          this.viewComponentRef.instance.entity = this.entity;
          this.viewComponentRef.instance.activity = this.activity;
          this.viewComponentRef.instance.persistChildComponent = this
            .viewComponentRef.instance.persistChildComponent
            ? this.viewComponentRef.instance.persistChildComponent
            : async function() {
                return new Promise(resolve => {
                  resolve(null);
                });
              };
        }
        this.form.removeControl(this.entity.templateCode);
      } else {
        if (!this.form.controls[this.entity.templateCode]) {
          this.form.addControl(
            this.entity.templateCode,
            ValidationService.createEntityValidationGroup(this.entity)
          );
        }

        const compType: Type<DataEntityInputComponent> = DataEntityFactory.createInputComponent(
          this.entity,
          null
        );
        if (compType && this.entityContainer) {
          this.inputComponentRef = this.entityContainer.createComponent(
            compType
          );
          this.inputComponentRef.instance.form = this.form;
          this.inputComponentRef.instance.entity = this.entity;
          this.inputComponentRef.instance.applicationId = this.applicationId;
          this.inputComponentRef.instance.activity = this.activity;
          this.inputComponentRef.instance.calculateActivity.subscribe(e => {
            this.calculateActivity.emit(e);
          });
          this.inputComponentRef.instance.isPreview = this.isPreview;
          this.inputComponentRef.instance.persistChildComponent = this
            .inputComponentRef.instance.persistChildComponent
            ? this.inputComponentRef.instance.persistChildComponent
            : async function() {
                return new Promise(resolve => {
                  resolve(null);
                });
              };
        }
      }
    }
  }
}
